import { markSectionViewed } from '../lib/http'; // adjust relative path if needed
import React, { useCallback, useTransition, useEffect, useMemo, useState, useRef } from 'react';
import '../styles/newsletter-signup.css';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import SEO, { generateCourseSEO } from '../components/SEO';
import CourseLearnView from '../features/courses/components/CourseLearnView';
import CourseCompletionModal from '../features/courses/components/CourseCompletionModal';
import { useCourseLearn } from '../features/courses/hooks/useCourseLearn';
import { useSectionView } from '../features/courses/hooks/useSectionView';
import { useQuiz } from '../features/courses/hooks/useQuiz';
import { useKnowledgeCheck } from '../features/courses/hooks/useKnowledgeCheck';
import { useCelebration } from '../features/celebration';
import { AUTH_ENABLED } from '../auth/flags';
import { Snackbar } from '../components/ui/Snackbar';

const VIEWED_SECTIONS_KEY = 'viewedSections';

function loadViewedSectionsForCourse(courseId) {
  if (!courseId) return new Set();
  try {
    const raw = window.localStorage.getItem(VIEWED_SECTIONS_KEY);
    if (!raw) return new Set();
    const map = JSON.parse(raw);
    const arr = Array.isArray(map?.[courseId]) ? map[courseId] : [];
    return new Set(arr);
  } catch {
    return new Set();
  }
}

function saveViewedSection(courseId, sectionId) {
  if (!courseId || !sectionId) return;
  try {
    const raw = window.localStorage.getItem(VIEWED_SECTIONS_KEY);
    const map = raw ? JSON.parse(raw) : {};
    const current = Array.isArray(map[courseId]) ? map[courseId] : [];
    if (!current.includes(sectionId)) {
      map[courseId] = [...current, sectionId];
      window.localStorage.setItem(VIEWED_SECTIONS_KEY, JSON.stringify(map));
    }
  } catch {
    // ignore storage errors
  }
}

export default React.memo(function CourseLearnPage() {
  const { courseId, sectionId: sectionParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, ready, enabled } = useAuth();
  const authEnabled = AUTH_ENABLED && !!enabled;
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const { celebrateCourseComplete, celebrateQuizPass } = useCelebration();

  const [localViewed, setLocalViewed] = useState(() => loadViewedSectionsForCourse(courseId));

  useEffect(() => {
    setLocalViewed(loadViewedSectionsForCourse(courseId));
  }, [courseId]);

  const {
    outline, loadingOutline,
    lessonView, loadingLesson,
    selectedSectionId, setSelectedSectionId,
    status, ownershipWarning,
    lessonId, loadLessonAndSelect, goPrev, goNext,
    isCourseCompleted,
  } = useCourseLearn({
    courseId,
    initialSectionId: sectionParam || null,
    authEnabled,
    user,
    ready,
    onNewContent: () => setSnackbarOpen(true),
  });

  // Course completion modal state
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const hasShownCompletionModalRef = useRef(false);

  // Course title stashed by the dashboard/search on the way in. Used as the
  // header/rail title until the outline resolves; kept separate from the
  // "this course" copy fallback so the rail never renders that phrase.
  const storedCourseTitle = useMemo(() => {
    try {
      return sessionStorage.getItem('ui.currentCourseTitle') || '';
    } catch {
      return '';
    }
  }, []);
  const courseName = storedCourseTitle || 'this course';

  // Unified section view - fetches polymorphic content for all section types
  const {
    quizData,
    knowledgeCheckData,
  } = useSectionView({
    sectionId: selectedSectionId,
    courseId,
    authEnabled,
  });

  // Keep UI responsive while data loads; don't blank existing content.
  const [, startTransition] = useTransition();

  // Single, stable way to change sections + URL.
  const goSectionWithUrl = useCallback((sid) => {
    if (!sid) return;
    if (sid === selectedSectionId) return; // no-op if already on it

    startTransition(() => {
      setSelectedSectionId(sid); // instant highlight in sidebar
      // Keep URL canonical & shareable without forcing a remount.
      const nextUrl = `/courses/${courseId}/sections/${sid}`;
      if (location.pathname !== nextUrl) navigate(nextUrl); // avoid double-navigate
    });
  }, [courseId, location.pathname, navigate, selectedSectionId, setSelectedSectionId]);

  // Click from sidebar (or in-view nav) — only fetch when lesson actually changes.
  const onClickLessonSection = useCallback(
    (lessonIdClicked, sectionIdClicked, currentLessonId) => {
      if (!sectionIdClicked) return;

      if (currentLessonId !== lessonIdClicked) {
        // Crossing lesson boundary: fetch new lesson + select section,
        // but DO NOT clear current lessonView first (handled in hook; see note below).
        startTransition(() => {
          loadLessonAndSelect(lessonIdClicked, sectionIdClicked);
        });
      } else {
        // Same lesson: just move the selected section (no network).
        goSectionWithUrl(sectionIdClicked);
      }
    },
    [goSectionWithUrl, loadLessonAndSelect, startTransition]
  );

  // Quiz hook - receives pre-loaded data from unified endpoint
  const quizState = useQuiz({
    courseId,
    lessonId,
    initialData: quizData,  // Pre-loaded from /sections/view
    onCourseComplete: celebrateCourseComplete,
    onQuizPass: celebrateQuizPass,
  });

  // Knowledge Check hook - receives pre-loaded data from unified endpoint
  const knowledgeCheckState = useKnowledgeCheck({
    lessonId,
    initialData: knowledgeCheckData,  // Pre-loaded from /sections/view
    onCourseComplete: celebrateCourseComplete,
  });

  // Build a lookup of viewed sectionIds from the outline (for persistence on reload)
  const viewedSectionIds = useMemo(() => {
    const ids = new Set();
    const lessons = outline?.lessons || [];
    for (const lesson of lessons) {
      const secs = lesson?.sections || [];
      for (const sec of secs) {
        const base = sec?.section ?? sec;
        if (base?.sectionId && base.viewed) {
          ids.add(base.sectionId);
        }
      }
    }
    return ids;
  }, [outline]);

  // Derive the currently selected section from the loaded lesson view
  const currentSection = useMemo(() => {
    const items = lessonView?.sections || [];
    const found = items.find(s => (s?.section?.sectionId ?? s?.sectionId) === selectedSectionId);
    if (!found) return null;
    const base = found?.section ?? found ?? null;
    if (!base) return null;
    // Ensure viewed flag is carried through regardless of where the API places it
    const viewed =
      base.viewed !== undefined ? base.viewed
        : found.viewed !== undefined ? found.viewed
          : viewedSectionIds.has(base.sectionId) ? true
            : localViewed.has(base.sectionId) ? true
              : false;
    return { ...base, viewed };
  }, [lessonView?.sections, selectedSectionId, viewedSectionIds, localViewed]);

  // NOTE: Quiz and Knowledge Check initialization is now handled automatically
  // via the unified /sections/view endpoint. The useSectionView hook fetches
  // pre-loaded data and passes it to useQuiz/useKnowledgeCheck as initialData.
  // The old effect-based initialization has been removed.

  // Show completion modal when course is completed
  useEffect(() => {
    // Case 1: Just completed the course via quiz
    if (quizState.courseJustCompleted) {
      setShowCompletionModal(true);
      hasShownCompletionModalRef.current = true;
      return;
    }

    // Case 2: Visiting an already completed course
    if (isCourseCompleted && !loadingOutline && !hasShownCompletionModalRef.current) {
      setShowCompletionModal(true);
      hasShownCompletionModalRef.current = true;
    }
  }, [quizState.courseJustCompleted, isCourseCompleted, loadingOutline]);

  // Reset modal shown flag when courseId changes
  useEffect(() => {
    hasShownCompletionModalRef.current = false;
  }, [courseId]);

  const [marking, setMarking] = useState(false);
  const handleCompleteSection = useCallback(async (nextSectionInfo) => {
    if (!currentSection || currentSection.contentType === 'QUIZ' || currentSection.contentType === 'KNOWLEDGE_CHECK') return;
    if (!lessonId || !selectedSectionId || !courseId) return;
    try {
      setMarking(true);
      const response = await markSectionViewed({
        courseId,
        lessonId,
        sectionId: selectedSectionId,
      });
      setLocalViewed(prev => {
        const next = new Set(prev);
        next.add(selectedSectionId);
        return next;
      });
      saveViewedSection(courseId, selectedSectionId);

      // Check if course was just completed
      if (response?.courseJustCompleted) {
        celebrateCourseComplete();
      }

      // Auto-navigate to next section if available
      if (nextSectionInfo) {
        onClickLessonSection(
          nextSectionInfo.lessonId,
          nextSectionInfo.sectionId,
          lessonId
        );
      } else {
        // No next section - refresh current to show completion state
        await loadLessonAndSelect(lessonId, selectedSectionId, { force: true });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to mark section viewed', e);
    } finally {
      setMarking(false);
    }
  }, [courseId, lessonId, selectedSectionId, currentSection, loadLessonAndSelect, onClickLessonSection, celebrateCourseComplete]);

  // Handle knowledge check completion - only update localViewed when backend confirms pass
  const handleKnowledgeCheckComplete = useCallback((sectionId) => {
    if (!sectionId || !courseId) return;
    setLocalViewed(prev => {
      const next = new Set(prev);
      next.add(sectionId);
      return next;
    });
    saveViewedSection(courseId, sectionId);
  }, [courseId]);

  // Generate course-specific SEO from outline.
  // `outline.courseTitle` can arrive as the backend's literal loading
  // placeholder ("(title loaded in lesson view)") before the real title
  // resolves — never let that (or the `courseName` "this course" fallback)
  // reach the tab title. Prefer the sessionStorage title stashed on the way
  // in, which is reliable, and fall back to a clean generic otherwise.
  const courseSeo = useMemo(() => {
    const rawTitle = outline?.courseTitle;
    const safeCourseTitle =
      rawTitle && rawTitle !== '(title loaded in lesson view)'
        ? rawTitle
        : (storedCourseTitle || 'Course');
    const seoTitle = currentSection?.title
      ? `${currentSection.title} — ${safeCourseTitle}`
      : safeCourseTitle;
    if (!outline) return { title: 'Course', noindex: true };
    return generateCourseSEO({
      id: courseId,
      title: seoTitle,
      description: outline.description,
      sections: outline.lessons,
    });
  }, [outline, courseId, storedCourseTitle, currentSection]);

  return (
    <div className="course-learn-page">
      <SEO {...courseSeo} />
      <CourseLearnView
        outline={outline}
        loadingOutline={loadingOutline}
        lessonView={lessonView}
        loadingLesson={loadingLesson}
        selectedSectionId={selectedSectionId}
        setSelectedSectionId={goSectionWithUrl}
        status={status}
        ownershipWarning={ownershipWarning}
        onSelectNavPrev={goPrev}
        onSelectNavNext={goNext}
        onClickLessonSection={onClickLessonSection}
        courseId={courseId}
        lessonId={lessonId}
        courseName={storedCourseTitle}
        currentSection={currentSection}
        quizState={quizState}
        knowledgeCheckState={knowledgeCheckState}
        onCompleteSection={handleCompleteSection}
        markingComplete={marking}
        localViewed={localViewed}
        onKnowledgeCheckComplete={handleKnowledgeCheckComplete}
      />

      <Snackbar
        open={snackbarOpen}
        message="New content added to this course!"
        onClose={() => setSnackbarOpen(false)}
      />

      <CourseCompletionModal
        isOpen={showCompletionModal}
        courseName={courseName}
        onClose={() => setShowCompletionModal(false)}
      />
    </div>
  );
});
